import { AddButton } from "@/components/common/AddButton";
import { BackdropIndicator } from "@/components/common/BackdropIndicator";
import { DeleteButton } from "@/components/sleep_checkup_v1/DeleteButton";
import { EditButton } from "@/components/sleep_checkup_v1/EditButton";
import { SCDialogTitle } from "@/components/sleep_checkup_v1/SCDialogTitle";
import { SCSelect } from "@/components/sleep_checkup_v1/SCSelect";
import { SCSnackbar } from "@/components/sleep_checkup_v1/SCSnackbar";
import {
  Pagination,
  SCEmptyTable,
  SCTable,
} from "@/components/sleep_checkup_v1/SCTable";
import { SleepCheckupUserStore } from "@/utils/auth";
import { orgAxios } from "@/utils/axios";
import { Add, Check } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { AxiosError } from "axios";
import { SyntheticEvent, useCallback, useEffect, useState } from "react";
import { useRefreshMe } from "./OrgLayout";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Role {
  code: string;
  name: string;
  description: string;
}

interface MemberRole {
  code: string;
  name: string;
}

type MemberStatus = "invited" | "pending_activation" | "registered";

interface Member {
  user_id: number;
  username: string;
  name: string;
  email: string;
  role: MemberRole;
  status: MemberStatus;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: "success" | "error";
  showIcon?: boolean;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

async function fetchRoles(): Promise<Role[]> {
  const res = await orgAxios.get("/api/org/roles/");
  return res.data.roles;
}

interface MembersResponse {
  count: number;
  page_size: number;
  current: number;
  results: Member[];
}

async function fetchMembers(
  page: number,
  pageSize: number
): Promise<MembersResponse> {
  const res = await orgAxios.get("/api/org/members/", {
    params: { page, page_size: pageSize },
  });
  return res.data;
}

async function inviteMember(data: {
  username: string;
  last_name: string;
  first_name: string;
  email: string;
  role: string;
}): Promise<Member> {
  const res = await orgAxios.post("/api/org/members/", data);
  return res.data;
}

async function updateMember(
  userId: number,
  data: {
    last_name: string;
    first_name: string;
    email: string;
    role: string;
  }
): Promise<void> {
  await orgAxios.put(`/api/org/members/${userId}/`, data);
}

async function deleteMember(userId: number): Promise<void> {
  await orgAxios.delete(`/api/org/members/${userId}/`);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type FieldErrors = Record<string, string[]>;

function extractFieldErrors(error: unknown): FieldErrors {
  if (error instanceof AxiosError && error.response?.data?.field_errors) {
    return error.response.data.field_errors as FieldErrors;
  }
  return {};
}

function extractErrorMessages(error: unknown): string {
  if (error instanceof AxiosError && error.response?.data?.error_messages) {
    return (error.response.data.error_messages as string[]).join("\n");
  }
  return "エラーが発生しました。";
}

function extractErrors(error: unknown): {
  fieldErrors: FieldErrors;
  generalError: string;
} {
  const fieldErrors = extractFieldErrors(error);
  if (Object.keys(fieldErrors).length > 0) {
    return { fieldErrors, generalError: "" };
  }
  return { fieldErrors: {}, generalError: extractErrorMessages(error) };
}

// ---------------------------------------------------------------------------
// Table headers
// ---------------------------------------------------------------------------

const TABLE_HEADERS = ["ユーザー名", "氏名", "権限", "メールアドレス", "操作"];

// ---------------------------------------------------------------------------
// MemberFormDialog (create / edit)
// ---------------------------------------------------------------------------

type MemberFormMode = "create" | "edit";

function MemberFormDialog({
  open,
  mode,
  member,
  roles,
  isSelf,
  onClose,
  onSuccess,
  onDelete,
}: {
  open: boolean;
  mode: MemberFormMode;
  member: Member | null;
  roles: Role[];
  isSelf: boolean;
  onClose: () => void;
  onSuccess: (username: string) => void;
  onDelete: (username: string) => void;
}) {
  const ROW_SPACING = 4;
  const COLUMN_SPACING = 8;
  const FIELD_WIDTH = "200px";
  const FIELD_HEIGHT = "52px";
  const ASTERISK_SX = {
    "& .MuiInputLabel-root .MuiInputLabel-asterisk": {
      marginLeft: "-2px",
    },
  };
  const SX = {
    width: FIELD_WIDTH,
    minHeight: FIELD_HEIGHT,
    ...ASTERISK_SX,
  };

  const nameParts = (member?.name ?? "").split(" ");
  const [username, setUsername] = useState(member?.username ?? "");
  const [lastName, setLastName] = useState(nameParts[0] ?? "");
  const [firstName, setFirstName] = useState(nameParts.slice(1).join(" "));
  const [email, setEmail] = useState(member?.email ?? "");
  const [role, setRole] = useState(member?.role.code ?? roles[0]?.code ?? "");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [generalError, setGeneralError] = useState("");
  const [isOpenConfirmation, setIsOpenConfirmation] = useState(false);

  const fieldError = (field: string): string | undefined =>
    fieldErrors[field]?.join(" ");

  const handleSubmit = async () => {
    setFieldErrors({});
    setGeneralError("");
    try {
      if (mode === "create") {
        await inviteMember({
          username,
          last_name: lastName,
          first_name: firstName,
          email,
          role,
        });
      } else {
        await updateMember(member!.user_id, {
          last_name: lastName,
          first_name: firstName,
          email,
          role,
        });
      }
      onSuccess(username);
    } catch (error) {
      const errors = extractErrors(error);
      setFieldErrors(errors.fieldErrors);
      setGeneralError(errors.generalError);
    }
  };

  const handleDeleteConfirmed = async () => {
    try {
      await deleteMember(member!.user_id);
      setIsOpenConfirmation(false);
      onDelete(member!.username);
    } catch (error) {
      setIsOpenConfirmation(false);
      setFieldErrors({});
      setGeneralError(extractErrorMessages(error));
    }
  };

  const isCreate = mode === "create";

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <SCDialogTitle onClickClose={onClose}>
          {isCreate ? "新しいユーザーを登録" : "ユーザー情報を編集"}
        </SCDialogTitle>
        <DialogContent>
          <Stack spacing={8}>
            {generalError && (
              <Alert severity="error">
                <Typography
                  sx={{
                    color: "error.main",
                    fontSize: "14px",
                    maxWidth: "366px",
                  }}
                >
                  {generalError}
                </Typography>
              </Alert>
            )}
            <Stack spacing={ROW_SPACING}>
              <TextField
                variant="standard"
                label="ユーザー名"
                required
                sx={{ minHeight: FIELD_HEIGHT, ...ASTERISK_SX }}
                disabled={!isCreate}
                value={username}
                onChange={
                  isCreate ? (e) => setUsername(e.target.value) : undefined
                }
                error={!!fieldError("username")}
                helperText={fieldError("username")}
              />
              <Stack direction="row" spacing={COLUMN_SPACING}>
                <TextField
                  variant="standard"
                  label="姓"
                  required
                  sx={SX}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  error={!!fieldError("last_name")}
                  helperText={fieldError("last_name")}
                />
                <TextField
                  variant="standard"
                  label="名"
                  required
                  sx={SX}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  error={!!fieldError("first_name")}
                  helperText={fieldError("first_name")}
                />
              </Stack>
              <TextField
                variant="standard"
                label="Eメールアドレス"
                required
                sx={{ minHeight: FIELD_HEIGHT, ...ASTERISK_SX }}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!fieldError("email")}
                helperText={fieldError("email")}
              />
              <SCSelect
                label="権限"
                required
                value={role}
                sx={ASTERISK_SX}
                variant="standard"
                disabled={!isCreate && isSelf}
                onChange={(e) => setRole(e.target.value)}
                error={!!fieldError("role")}
                helperText={fieldError("role")}
              >
                {roles.map((r) => (
                  <MenuItem key={r.code} value={r.code}>
                    {r.name}
                  </MenuItem>
                ))}
              </SCSelect>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={
            isCreate
              ? { justifyContent: "flex-end" }
              : { justifyContent: "space-between" }
          }
        >
          {!isCreate && (
            <DeleteButton
              variant="text"
              disabled={isSelf}
              onClick={() => setIsOpenConfirmation(true)}
            >
              削除する
            </DeleteButton>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={isCreate ? <Add /> : <Check />}
            onClick={handleSubmit}
          >
            {isCreate ? "登録する" : "変更を保存"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除の確認ダイアログ */}
      {!isCreate && (
        <Dialog
          open={isOpenConfirmation}
          onClose={() => setIsOpenConfirmation(false)}
          PaperProps={{ sx: { borderRadius: "16px" } }}
        >
          <DialogTitle>ダッシュボードユーザーを削除</DialogTitle>
          <DialogContent>
            <Typography
              variant="body1"
              sx={{ minWidth: 380, color: "#282828" }}
            >
              本当にこのユーザーを削除しますか？
            </Typography>
          </DialogContent>
          <DialogActions sx={{ gap: "16px" }}>
            <Button
              onClick={() => setIsOpenConfirmation(false)}
              sx={{ fontSize: "14px" }}
            >
              キャンセル
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleDeleteConfirmed}
            >
              削除する
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// MemberTableRow
// ---------------------------------------------------------------------------

function MemberTableRow({
  member,
  onClickEdit,
}: {
  member: Member;
  onClickEdit: (member: Member) => void;
}) {
  return (
    <TableRow>
      <TableCell>{member.username}</TableCell>
      <TableCell>{member.name}</TableCell>
      <TableCell>{member.role.name}</TableCell>
      <TableCell>{member.email}</TableCell>
      <TableCell>
        <EditButton
          variant="text"
          size="small"
          onClick={() => onClickEdit(member)}
        />
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// OrgMemberList (main)
// ---------------------------------------------------------------------------

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

export default function OrgMemberList() {
  const refreshMe = useRefreshMe();
  const [members, setMembers] = useState<Member[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogMode, setDialogMode] = useState<MemberFormMode | null>(null);
  const [showIndicator, setShowIndicator] = useState(false);
  const [editTarget, setEditTarget] = useState<Member | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  const currentUser = SleepCheckupUserStore.getItem();
  const currentUsername = currentUser?.username ?? "";
  const isActivated = currentUser?.is_activated ?? false;

  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchMembers(page, pageSize);
      setMembers(res.results);
      setTotalCount(res.count);
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 403) {
        // 未有効化ユーザーは403が返る。メンバー一覧は空のまま表示
      } else {
        setSnackbar({
          open: true,
          message: extractErrorMessages(error),
          severity: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleOpenDialog = async (mode: MemberFormMode, member?: Member) => {
    refreshMe();
    try {
      setShowIndicator(true);
      const rolesData = await fetchRoles();
      setRoles(rolesData);
      setDialogMode(mode);
      setEditTarget(member ?? null);
    } catch (error) {
      setSnackbar({
        open: true,
        message: extractErrorMessages(error),
        severity: "error",
      });
    } finally {
      setShowIndicator(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogMode(null);
    setEditTarget(null);
  };

  const handleDialogSuccess = (username: string) => {
    const message =
      dialogMode === "create"
        ? "新しいユーザーが追加されました"
        : `${username} さんの情報を更新しました。`;
    handleCloseDialog();
    setSnackbar({ open: true, message, severity: "success" });
    loadMembers();
  };

  const handleDialogDelete = (username: string) => {
    handleCloseDialog();
    setSnackbar({
      open: true,
      message: `ユーザー ${username} を削除しました`,
      severity: "success",
      showIcon: true,
    });
    if (members.length <= 1 && page > 1) {
      setPage(page - 1);
    } else {
      loadMembers();
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setPageSize(newRowsPerPage);
    setPage(1);
  };

  const handleSnackbarClose = (
    _event: SyntheticEvent<Element, Event> | Event,
    reason: string
  ) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ px: 7, py: 6 }}>
      <Stack spacing={6} sx={{ mb: "40px" }}>
        <Typography variant="h5">ユーザー一覧</Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <AddButton
            onClick={() => handleOpenDialog("create")}
            disabled={!isActivated}
          >
            新しいユーザーを登録
          </AddButton>
        </Box>
      </Stack>

      {loading ? (
        <BackdropIndicator open={loading} />
      ) : !isActivated || members.length === 0 ? (
        <SCEmptyTable
          headers={TABLE_HEADERS}
          message={
            isActivated
              ? "メンバーがいません。"
              : "現在表示できる項目がありません"
          }
          subMessage={
            !isActivated ? (
              <Typography variant="body1" sx={{ color: "#A3A3A3" }}>
                アカウントの有効化手続き中です。
                <br />
                担当者からの電話による疎通確認が完了するまで、
                <br />
                ユーザー管理にはアクセスできません。
              </Typography>
            ) : undefined
          }
        />
      ) : (
        <SCTable<Member>
          headers={TABLE_HEADERS}
          list={members}
          pagination={new Pagination(pageSize, page, totalCount)}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          tableRow={(member, i) => (
            <MemberTableRow
              key={member.user_id}
              member={member}
              onClickEdit={(m) => handleOpenDialog("edit", m)}
            />
          )}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      )}

      {/* Create / Edit Dialog */}
      {dialogMode != null && (
        <MemberFormDialog
          open={dialogMode != null}
          mode={dialogMode}
          member={editTarget}
          roles={roles}
          isSelf={editTarget?.username === currentUsername}
          onClose={handleCloseDialog}
          onSuccess={handleDialogSuccess}
          onDelete={handleDialogDelete}
        />
      )}

      {/* Snackbar */}
      <SCSnackbar
        open={snackbar.open}
        severity={snackbar.severity}
        icon={snackbar.showIcon ? undefined : false}
        onClose={handleSnackbarClose}
      >
        {snackbar.message}
      </SCSnackbar>

      <BackdropIndicator open={showIndicator} />
    </Box>
  );
}
